import { baseApi } from '../../services/baseApi';
import { ApiEndPoints } from '../../constants/apiEndPoints';

/** A designation row within a government department. */
export interface ApiDesignation {
  id?: number;
  name: string;
  rank: number;
  territory_level?: string;
  is_lowest_level: boolean;
  /** "Primary" | "Secondary" — this post's control mode within the department. */
  control_mode?: string | null;
  /** "Elected" | "Official" — the post's nature. */
  designation_type?: string | null;
}

/** A government department as returned by the backend. */
export interface GovtDepartment {
  id: number;
  name: string;
  description?: string | null;
  territory_hierarchy?: string | null;
  control_mode?: 'direct' | 'dual' | 'autonomous' | null;
  short_code?: string | null;
  status?: 'Active' | 'Inactive' | null;
  /** Convenience boolean (status === "Active"). */
  active?: boolean;
  /** Convenience count of designations. */
  levels?: number;
  organization?: number | null;
  designations?: ApiDesignation[];
  created_at?: string;
  updated_at?: string;
}

/** Body accepted by create/update (all optional on PATCH). */
export interface GovtDepartmentPayload {
  name: string;
  description?: string;
  territory_hierarchy?: string;
  control_mode?: 'direct' | 'dual' | 'autonomous';
  short_code?: string;
  status?: 'Active' | 'Inactive';
  organization?: number;
  designations?: ApiDesignation[];
}

/** DRF list responses may be a plain array or a paginated `{ results }` envelope. */
type Listish<T> = T[] | { count?: number; results?: T[] };
const asList = <T>(r: Listish<T>): T[] => (Array.isArray(r) ? r : (r?.results ?? []));

/** Normalized paginated list the department table consumes. */
export interface DepartmentsResponse {
  items: GovtDepartment[];
  total: number;
  page: number;
  pageSize: number;
}

/** A standard department in the Create-Org step-3 catalog. */
export interface CatalogDepartment {
  code: string;
  name: string;
  category: string;
}

export interface DepartmentListArgs {
  search?: string;
  status?: string;
  territory_hierarchy?: string;
  organization?: number;
  ordering?: string;
  page?: number;
  pageSize?: number;
}

export const departmentsApi = baseApi.injectEndpoints({
  endpoints: (build) => ({
    getDepartments: build.query<DepartmentsResponse, DepartmentListArgs | void>({
      query: (arg) => ({
        url: `${ApiEndPoints.govtDepartment}/`,
        params: {
          ...(arg?.search ? { search: arg.search } : {}),
          ...(arg?.status ? { status: arg.status } : {}),
          ...(arg?.territory_hierarchy ? { territory_hierarchy: arg.territory_hierarchy } : {}),
          ...(arg?.organization != null ? { organization: arg.organization } : {}),
          ...(arg?.ordering ? { ordering: arg.ordering } : {}),
          page: arg?.page ?? 1,
          page_size: arg?.pageSize ?? 10,
        },
      }),
      transformResponse: (r: Listish<GovtDepartment>, _meta, arg) => {
        const items = asList(r);
        const total = Array.isArray(r) ? r.length : (r?.count ?? items.length);
        return { items, total, page: arg?.page ?? 1, pageSize: arg?.pageSize ?? 10 };
      },
      providesTags: ['GovtDepartments'],
    }),
    getDepartment: build.query<GovtDepartment, number | string>({
      query: (id) => ({ url: `${ApiEndPoints.govtDepartment}/${id}/` }),
      providesTags: ['GovtDepartments'],
    }),
    getDepartmentFilters: build.query<{ territory_hierarchies: string[] }, void>({
      query: () => ({ url: `${ApiEndPoints.govtDepartmentFilters}/` }),
      transformResponse: (r: { territory_hierarchies?: string[] } | null) => ({
        territory_hierarchies: r?.territory_hierarchies ?? [],
      }),
    }),
    createDepartment: build.mutation<GovtDepartment, GovtDepartmentPayload>({
      query: (body) => ({ url: `${ApiEndPoints.govtDepartment}/`, method: 'POST', body }),
      invalidatesTags: ['GovtDepartments'],
    }),
    updateDepartment: build.mutation<
      GovtDepartment,
      { id: number | string; patch: Partial<GovtDepartmentPayload> }
    >({
      query: ({ id, patch }) => ({
        url: `${ApiEndPoints.govtDepartment}/${id}/`,
        method: 'PATCH',
        body: patch,
      }),
      invalidatesTags: ['GovtDepartments'],
    }),
    deleteDepartment: build.mutation<void, number | string>({
      query: (id) => ({ url: `${ApiEndPoints.govtDepartment}/${id}/`, method: 'DELETE' }),
      invalidatesTags: ['GovtDepartments'],
    }),
    // Create-Org step 3 — the 25 standard departments to pick from.
    getDepartmentCatalog: build.query<CatalogDepartment[], void>({
      query: () => ({ url: `${ApiEndPoints.govtDepartmentCatalog}/` }),
      transformResponse: (r: { departments?: CatalogDepartment[] } | CatalogDepartment[] | null) =>
        Array.isArray(r) ? r : (r?.departments ?? []),
    }),
    // Create-Org step 3 — save the org's picked department codes.
    selectDepartments: build.mutation<unknown, { organization: number; codes: string[] }>({
      query: (body) => ({ url: `${ApiEndPoints.govtDepartmentSelect}/`, method: 'POST', body }),
      invalidatesTags: ['GovtDepartments'],
    }),
  }),
});

export const {
  useGetDepartmentsQuery,
  useGetDepartmentQuery,
  useGetDepartmentFiltersQuery,
  useCreateDepartmentMutation,
  useUpdateDepartmentMutation,
  useDeleteDepartmentMutation,
  useGetDepartmentCatalogQuery,
  useSelectDepartmentsMutation,
} = departmentsApi;
